import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Row, Col, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import Layout from '../components/Layout';
import FormPageTitle from '../components/FormPageTitle';
import Pagination from '../components/Pagination';
import { promocodeTemplateService, PromocodeTemplate } from '../services/promocodeTemplateService';
import { authService, User } from '../services/authService';
import { API_BASE_URL, STORAGE_PATHS, ROUTES, DEFAULTS } from '../constants';
import { useTranslation } from '../contexts/LocaleContext';
import { getGifDurationSeconds } from '../utils/gifDuration';
import { fontService, Font } from '../services/fontService';

type MediaScaleResult = { scaleX: number; scaleY: number; naturalWidth: number; naturalHeight: number } | null;

function useMediaScale(
  ref: React.RefObject<HTMLImageElement | HTMLVideoElement | null>,
  isVideo: boolean
): MediaScaleResult {
  const [state, setState] = useState<MediaScaleResult>(null);
  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const w = isVideo ? (el as HTMLVideoElement).videoWidth : (el as HTMLImageElement).naturalWidth;
    const h = isVideo ? (el as HTMLVideoElement).videoHeight : (el as HTMLImageElement).naturalHeight;
    if (w && h) setState({ scaleX: el.offsetWidth / w, scaleY: el.offsetHeight / h, naturalWidth: w, naturalHeight: h });
  }, [ref, isVideo]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (isVideo) {
      const v = el as HTMLVideoElement;
      if (v.readyState >= 2) update();
      else v.addEventListener('loadedmetadata', update);
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => {
        v.removeEventListener('loadedmetadata', update);
        ro.disconnect();
      };
    } else {
      const img = el as HTMLImageElement;
      if (img.complete && img.naturalWidth) update();
      else img.addEventListener('load', update);
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => {
        img.removeEventListener('load', update);
        ro.disconnect();
      };
    }
  }, [ref, isVideo, update]);
  return state;
}

/** Оверлей в координатах отображения: без scale на обёртке, чтобы текст не растягивался; рамка вокруг CODE */
const TemplatePreviewOverlay: React.FC<{
  mediaSize: MediaScaleResult;
  data: any;
  visible: boolean;
  fonts?: Font[];
}> = ({ mediaSize, data, visible, fonts }) => {
  const d = data;
  const hasArea = d && Number(d.width) > 0 && Number(d.height) > 0;
  if (!mediaSize || !hasArea) return null;

  const { scaleX, scaleY, naturalWidth, naturalHeight } = mediaSize;
  const x = Number(d.x) || 0;
  const y = Number(d.y) || 0;
  const width = Number(d.width) || 0;
  const height = Number(d.height) || 0;
  const angle = Number(d.rotationAngle ?? d.angle) || 0;
  const withSubstrate = d.with_substrate ?? false;
  const renderHeader = d.render_header ?? false;
  const headerText = d.header_text ?? DEFAULTS.PROMOCODE_TEMPLATE.HEADER_TEXT;
  const headerColor = d.header_color ?? DEFAULTS.PROMOCODE_TEMPLATE.HEADER_COLOR;
  const color = d.textColor ?? d.color ?? DEFAULTS.PROMOCODE_TEMPLATE.COLOR;

  const displayW = naturalWidth * scaleX;
  const displayH = naturalHeight * scaleY;
  const scaleMin = Math.min(scaleX, scaleY);

  const wrapperStyle: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    top: 0,
    width: displayW,
    height: displayH,
    pointerEvents: 'none',
    zIndex: 5,
    visibility: visible ? 'visible' : 'hidden',
  };

  const canvasStyle: React.CSSProperties = {
    position: 'absolute',
    left: x * scaleX,
    top: y * scaleY,
    width: width * scaleX,
    height: height * scaleY,
    transform: `rotate(${angle}deg)`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 100,
    border: '2px dashed rgba(255, 220, 0, 0.85)',
    boxSizing: 'border-box',
    ...(withSubstrate
      ? {
          background: d.substrate_background_color ?? DEFAULTS.PROMOCODE_TEMPLATE.SUBSTRATE_BACKGROUND_COLOR,
          borderRadius: `${(d.upper_radius ?? 1) * scaleMin}px / ${(d.sides_radius ?? 1) * scaleMin}px`,
          boxShadow: `-${(d.shadow_upper_size ?? 10) * scaleMin}px -${(d.shadow_sides_size ?? 10) * scaleMin}px 10px 1px rgba(0,0,0,0.3)`,
        }
      : {}),
  };

  const headerStyle: React.CSSProperties = {
    position: 'absolute',
    left: (x + width / 4) * scaleX,
    top: (y - 50) * scaleY,
    fontSize: Math.max(10, (width / 12) * scaleMin) + 'px',
    color: headerColor,
    transform: `rotate(${angle}deg)`,
    zIndex: 130,
    margin: 0,
  };

  // Та же логика, что при создании шаблона: влезает и по ширине, и по высоте
  const fontSizeByWidth = width / 3;
  const fontSizeByHeight = height / 1.3;
  const codeFontSize = Math.max(10, Math.min(fontSizeByWidth, fontSizeByHeight) * scaleMin);

  const fontId = d?.fontId != null ? Number(d.fontId) : 0;
  const selectedFont = fonts?.find((f) => f.id === fontId);
  const codeFontClass = selectedFont
    ? `custom-font-${(selectedFont.name || '').replace(/[\s.]/g, '') || `font-${selectedFont.id}`}`
    : '';

  return (
    <div style={wrapperStyle}>
      {renderHeader && <p style={headerStyle}>{headerText}</p>}
      <div style={canvasStyle}>
        <span
          className={codeFontClass || undefined}
          style={{
            color,
            fontSize: codeFontSize + 'px',
          }}
        >
          CODE
        </span>
      </div>
    </div>
  );
};

/** Превью изображения: как в шаблоне, для GIF — учёт времени показа */
const TemplateImagePreview: React.FC<{ template: PromocodeTemplate; srcFull: string; fonts?: Font[] }> = ({ template, srcFull, fonts }) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const mediaSize = useMediaScale(imgRef, false);
  const d = template.data;
  const isGif = template.file?.mime_type?.includes('gif') ?? false;
  const [gifDuration, setGifDuration] = useState(0);
  const [gifCurrentTime, setGifCurrentTime] = useState(0);

  useEffect(() => {
    if (!isGif || !srcFull) return;
    getGifDurationSeconds(srcFull)
      .then((dur) => setGifDuration(dur > 0 ? dur : 0))
      .catch(() => setGifDuration(0));
  }, [isGif, srcFull]);

  useEffect(() => {
    if (!isGif || gifDuration <= 0) return;
    setGifCurrentTime(0);
    const step = 0.05;
    const id = setInterval(() => {
      setGifCurrentTime((prev) => (prev + step >= gifDuration ? 0 : prev + step));
    }, step * 1000);
    return () => clearInterval(id);
  }, [isGif, gifDuration]);

  const startSec = Number(d?.startTimeSeconds ?? d?.start_time) || 0;
  const endVal = d?.endTimeSeconds ?? d?.end_time;
  const endSec = (endVal != null && endVal !== '' && Number.isFinite(Number(endVal))) ? Number(endVal) : gifDuration;
  const hasValidRange = gifDuration > 0 && endSec > startSec;
  const showOverlay = !isGif || !hasValidRange || (gifCurrentTime >= startSec && gifCurrentTime <= endSec);

  return (
    <div style={{ position: 'relative', display: 'inline-block', maxWidth: '100%', overflow: 'hidden' }}>
      <img
        ref={imgRef}
        className="template-preview-media"
        src={srcFull}
        alt=""
        style={{ display: 'block', maxWidth: '100%', maxHeight: '360px', objectFit: 'contain', width: 'auto', height: 'auto' }}
      />
      <TemplatePreviewOverlay mediaSize={mediaSize} data={d} visible={showOverlay} fonts={fonts} />
    </div>
  );
};

/** Превью видео: как в шаблоне, учёт времени показа и адаптив */
const TemplateVideoPreview: React.FC<{ template: PromocodeTemplate; srcFull: string; fonts?: Font[] }> = ({ template, srcFull, fonts }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaSize = useMediaScale(videoRef as React.RefObject<HTMLImageElement | HTMLVideoElement | null>, true);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const d = template.data;

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onLoadedMetadata = () => {
      if (Number.isFinite(v.duration)) setVideoDuration(v.duration);
    };
    if (v.readyState >= 2) onLoadedMetadata();
    else v.addEventListener('loadedmetadata', onLoadedMetadata);
    return () => v.removeEventListener('loadedmetadata', onLoadedMetadata);
  }, [srcFull]);

  const startSec = Number(d?.startTimeSeconds ?? d?.start_time) || 0;
  const endVal = d?.endTimeSeconds ?? d?.end_time;
  const endSec = (endVal != null && endVal !== '' && Number.isFinite(Number(endVal))) ? Number(endVal) : videoDuration;
  const hasValidRange = videoDuration > 0 && endSec > startSec;
  const showOverlay = !hasValidRange || (videoCurrentTime >= startSec && videoCurrentTime <= endSec);

  return (
    <div style={{ position: 'relative', display: 'inline-block', maxWidth: '100%', overflow: 'hidden' }}>
      <video
        ref={videoRef}
        className="template-preview-media"
        src={srcFull}
        muted
        loop
        playsInline
        autoPlay
        onTimeUpdate={(e) => setVideoCurrentTime((e.target as HTMLVideoElement).currentTime)}
        style={{ display: 'block', maxWidth: '100%', maxHeight: '360px', objectFit: 'contain', width: 'auto', height: 'auto' }}
      />
      <TemplatePreviewOverlay mediaSize={mediaSize} data={d} visible={showOverlay} fonts={fonts} />
    </div>
  );
};

const PAGE_SIZE = 6;

const PromocodeTemplates: React.FC = () => {
  const t = useTranslation();
  const { id: categoryId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<PromocodeTemplate[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [fonts, setFonts] = useState<Font[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadUser();
  }, []);

  useEffect(() => {
    fontService.getFonts().then(setFonts).catch(() => setFonts([]));
  }, []);

  useEffect(() => {
    loadTemplates(currentPage, categoryId || null);
  }, [categoryId, currentPage]);

  const loadUser = async () => {
    const currentUser = await authService.getCurrentUser();
    setUser(currentUser);
  };


  const loadTemplates = async (page: number = 1, categoryId: string | null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await promocodeTemplateService.getTemplates(categoryId, page, PAGE_SIZE);
      setTemplates(response.data);
      setCurrentPage(page);
      setLastPage(response.last_page);
      setTotalCount(response.total);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError(t('promocode.notFound') || t('errors.loadTemplatesFailed'));
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('manage.delete') + '?')) return;
    try {
      await promocodeTemplateService.deleteTemplate(id);
      await loadTemplates(currentPage, categoryId || null);
    } catch (err) {
      console.error('Error deleting template:', err);
      alert(t('errors.deleteTemplate'));
    }
  };

  const isSuperAdmin = user?.roles?.includes('Admin') || false;
  const canCreate = user?.permissions?.includes('templates.create') || isSuperAdmin;
  const canEdit = user?.permissions?.includes('templates.update') || isSuperAdmin;
  const canDelete = user?.permissions?.includes('templates.delete') || isSuperAdmin;

  const imageSrc = (path: string) =>{
    return path?
    path.startsWith('http') ? path : `${API_BASE_URL}${path}` : ""}

  return (
    <Layout>
      <style>
        {fonts
          .filter((font) => font.file?.path)
          .map((font) => {
            const fontName = (font.name || '').replace(/[\s.]/g, '') || `font-${font.id}`;
            const fontUrl = font.file!.path.startsWith('http')
              ? font.file!.path
              : `${API_BASE_URL}${font.file!.path}`;
            return `
            @font-face {
              font-family: "${fontName}";
              src: url("${fontUrl}");
            }
            .custom-font-${fontName} {
              font-family: "${fontName}";
            }
          `;
          })}
      </style>
      {categoryId && (
        <Row className="my-2">
          <Col xs={12}>
            <Button
              variant="link"
              className="text-secondary text-decoration-none p-0 align-baseline"
              onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate(ROUTES.HOME);
                }
              }}
            >
              ← {t('manage.back')}
            </Button>
          </Col>
        </Row>
      )}
      <FormPageTitle title={t('templates')} helperContent={!categoryId ? t('helpers.promocodeTemplates.title') : undefined} />

      {canCreate && (
        <Row className="my-2 d-flex ">
          <Col className='p-0'>
            <Button
              variant="primary"
              onClick={() => navigate(ROUTES.PROMOCODE_TEMPLATE_CREATE)}
            >
              {t('promocode.create')}
            </Button>
          </Col>
        </Row>
      )}


      {loading && (
        <div className="d-flex justify-content-center my-4">
          <Spinner animation="border" role="status" />
        </div>
      )}

      {!loading && templates.length === 0 && (
        <div className="text-secondary text-center my-2">{t('promocode.notFound')}</div>
      )}

      {!loading && (
        <div className="mx-auto">
          <Row className="g-2">
            {templates.map((template) => (
              <Col key={template.id} xs={12} xl={4} lg={6}>
              <div className="p-2 text-bg-light border rounded h-100 d-flex flex-column">
                <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                  <div className="d-flex gap-2 py-1 flex-wrap">
                    <Badge bg="primary" className="">
                      {template.name}
                    </Badge>
                    <Badge bg="success" className="">
                      {template.type === 'image' ? t('promocode.image') : t('promocode.video')}
                    </Badge>
                    {template.data && (!template.data.x || !template.data.y || template.data.x === 0 || template.data.y === 0) && (
                      <Badge bg="danger" className="">
                        {t('promocode.waitingForSetup')}
                      </Badge>
                    )}
                    {template.user && (
                      <Badge bg="secondary" className="">
                        {t('createdBy')} {template.user.name}
                      </Badge>
                    )}
                  </div>
                  <div className="d-flex flex-row flex-wrap gap-2">
                    {canEdit && (
                      <Button
                        variant="success"
                        className=""
                        onClick={() =>
                          navigate(ROUTES.PROMOCODE_TEMPLATE_EDIT(template.id))
                        }
                      >
                        {t('manage.edit')}
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="danger"
                        className=""
                        onClick={() => handleDelete(template.id)}
                      >
                        {t('manage.delete')}
                      </Button>
                    )}
                  </div>
                </div>
                {template.categories && template.categories.length > 0 && (
                  <div className="d-flex gap-2 align-items-center pt-2 flex-wrap">
                    <span className="fw-semibold">{t('categoryName')}:</span>
                    <div className="d-flex gap-2 flex-wrap">
                      {template.categories.map((cat) => (
                        <Button
                          key={cat.id}
                          variant="primary"
                          size="sm"
                          onClick={() => navigate(ROUTES.CATEGORY_PROMOCODE_TEMPLATES(cat.id))}
                        >
                          {cat.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="d-flex justify-content-center align-items-center pt-2 "
                style={{flex:'1 1 auto'}}
                >
                  {template.type === 'video' && template.file && (() => {
                    const src = template.file.url ?? template.file.path ?? '';
                    return src ? (
                      <TemplateVideoPreview
                        template={template}
                        srcFull={src.startsWith('http') ? src : imageSrc(src)}
                        fonts={fonts}
                      />
                    ) : null;
                  })()}
                  {template.type === 'image' && template.file && (() => {
                    const src = template.file.url ?? template.file.path ?? '';
                    return src ? (
                      <TemplateImagePreview
                        template={template}
                        srcFull={src.startsWith('http') ? src : imageSrc(src)}
                        fonts={fonts}
                      />
                    ) : null;
                  })()}
                </div>
              </div>
              </Col>
            ))}
          </Row>
        </div>
      )}
      {!loading && lastPage > 1 && (
        <Pagination
          currentPage={currentPage}
          lastPage={lastPage}
          totalCount={totalCount}
          pageSize={PAGE_SIZE}
          onPageChange={(page) => setCurrentPage(page)}
        />
      )}
    </Layout>
  );
};

export default PromocodeTemplates;
